import axiosInstance from "@/lib/axiosInstance";
import type { CustomAxiosRequestConfig } from "@/types/common.types";
import { userManagementEndpoints } from "@/api/user-management/userManagement.endpoints";
import type { HolderCopy } from "./roleHolders";

/**
 * Adding and removing a role goes through the person, not the role: there is no
 * role-side membership endpoint and no bulk one, so each person is one request.
 *
 * The body carries only the two role lists. The backend applies the fields a PUT names and
 * leaves the rest of the user untouched, so nothing else about the person can be changed by
 * this call — in particular `entityIds`, which it would read as the person's complete entity
 * access and soft-delete every membership missing from it.
 */
export type RoleMembershipChange = {
  userId: string;
  addedRoleIds?: string[];
  removedRoleIds?: string[];
  /** The company to act in, when the role is an entity's own copy. */
  actingEntityId?: string;
};

export const changeUserRoles = async ({
  userId,
  addedRoleIds,
  removedRoleIds,
  actingEntityId,
}: RoleMembershipChange): Promise<void> => {
  await axiosInstance.put(
    `${userManagementEndpoints.users}/${userId}`,
    {
      ...(addedRoleIds?.length ? { addedRoleIds } : {}),
      ...(removedRoleIds?.length ? { removedRoleIds } : {}),
    },
    // The caller shows its own inline message in plain words. Without this the interceptor
    // raises a server-worded toast as well, and a modal would carry two alerts at once.
    {
      suppressErrorAlert: true,
      ...(actingEntityId ? { actingEntityId } : {}),
    } as CustomAxiosRequestConfig,
  );
};

/**
 * Takes a person off each copy in turn, each write acting in that copy's company so the backend
 * finds the role there (D251). Stops at the first refusal; a retry resends every copy, and one
 * already removed is a no-op.
 */
export const removeFromRoleCopies = async (userId: string, copies: HolderCopy[]): Promise<void> => {
  for (const copy of copies) {
    await changeUserRoles({
      userId,
      removedRoleIds: [copy.roleId],
      actingEntityId: copy.tenantId,
    });
  }
};

export type AddPeopleOutcome = {
  added: string[];
  failed: Array<{ userId: string; name: string }>;
};

/**
 * Adds a role to several people, one request each. Every request is attempted even when one
 * fails, so a failure part-way does not silently drop the people after it; the caller reports
 * who did not make it and keeps them selected.
 */
export const addPeopleToRole = async (
  roleId: string,
  people: Array<{ userId: string; name: string }>,
): Promise<AddPeopleOutcome> => {
  const results = await Promise.allSettled(
    people.map((person) => changeUserRoles({ userId: person.userId, addedRoleIds: [roleId] })),
  );

  const outcome: AddPeopleOutcome = { added: [], failed: [] };
  results.forEach((result, index) => {
    const person = people[index];
    if (result.status === "fulfilled") outcome.added.push(person.userId);
    else outcome.failed.push(person);
  });
  return outcome;
};
